'use client';

export const DEVICE_ID_COOKIE = 'gp_device_id';
export const DEVICE_ID_STORAGE_KEY = 'gp_device_id';
export const DEVICE_VERIFICATION_PENDING_COOKIE = 'gp_device_verification_pending';
export const DEVICE_APPROVAL_RECENT_COOKIE = 'gp_device_approval_recent';
export const DEVICE_APPROVAL_BYPASS_COOKIE = 'gp_device_approval_bypass';
export const DEVICE_TRUST_COOKIE = 'gp_device_trust';
export const DEVICE_REVOKED_COOKIE = 'gp_device_revoked';
export const SIGNUP_DEVICE_TRUST_COOKIE = 'gp_signup_device_trust';
const PENDING_DEVICE_ID_STORAGE_KEY = 'gp_device_id_pending';
const PENDING_DEVICE_ID_TTL_MS = 30_000;

const DEVICE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 3;
export const DEVICE_TRUST_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const DEFAULT_DEVICE_TRUST_HASH_SALT = 'greenpro-device-trust-v1';

const DEVICE_FLOW_COOKIES = [
  DEVICE_TRUST_COOKIE,
  DEVICE_REVOKED_COOKIE,
  DEVICE_APPROVAL_RECENT_COOKIE,
  DEVICE_APPROVAL_BYPASS_COOKIE,
  DEVICE_VERIFICATION_PENDING_COOKIE,
  SIGNUP_DEVICE_TRUST_COOKIE,
];

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const value = part.trim();
    if (value.startsWith(prefix)) {
      return decodeURIComponent(value.slice(prefix.length));
    }
  }

  return null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${DEVICE_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function clearCookie(name: string) {
  if (typeof document === 'undefined') return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  document.cookie = `${name}=; Path=/; Max-Age=0${secure}`;
}

function createDeviceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `gp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function persistDeviceId(deviceId: string) {
  const normalizedDeviceId = String(deviceId || '').trim();
  if (!normalizedDeviceId || typeof window === 'undefined') {
    return '';
  }

  try {
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, normalizedDeviceId);
  } catch {
    // Ignore storage write failures.
  }

  writeCookie(DEVICE_ID_COOKIE, normalizedDeviceId);
  return normalizedDeviceId;
}

export function setPendingDeviceId(deviceId: string, ttlMs = PENDING_DEVICE_ID_TTL_MS) {
  const normalizedDeviceId = String(deviceId || '').trim();
  if (!normalizedDeviceId || typeof window === 'undefined') {
    return '';
  }

  try {
    window.sessionStorage.setItem(
      PENDING_DEVICE_ID_STORAGE_KEY,
      JSON.stringify({
        deviceId: normalizedDeviceId,
        expiresAt: Date.now() + ttlMs,
      })
    );
  } catch {
    // Ignore storage write failures.
  }

  return normalizedDeviceId;
}

export function getPendingDeviceId() {
  if (typeof window === 'undefined') return '';

  const fromQuery = new URL(window.location.href).searchParams.get('gpdid')?.trim() || '';
  if (fromQuery) {
    return fromQuery;
  }

  try {
    const raw = window.sessionStorage.getItem(PENDING_DEVICE_ID_STORAGE_KEY);
    if (!raw) return '';

    const parsed = JSON.parse(raw);
    const deviceId = String(parsed?.deviceId || '').trim();
    const expiresAt = Number(parsed?.expiresAt || 0);

    if (!deviceId || expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(PENDING_DEVICE_ID_STORAGE_KEY);
      return '';
    }

    return deviceId;
  } catch {
    return '';
  }
}

export function appendPendingDeviceIdToPath(path: string) {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath || typeof window === 'undefined') {
    return normalizedPath;
  }

  const pendingDeviceId = getPendingDeviceId();
  if (!pendingDeviceId) {
    return normalizedPath;
  }

  const url = new URL(normalizedPath, window.location.origin);
  if (url.origin !== window.location.origin) {
    return normalizedPath;
  }

  if (!url.searchParams.get('gpdid')) {
    url.searchParams.set('gpdid', pendingDeviceId);
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

async function hashDeviceTokenWithSaltClient(deviceToken: string, salt: string) {
  const encoder = new TextEncoder();
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    encoder.encode(`${salt}:${deviceToken}`)
  );

  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

export function getDeviceId(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const fromStorage = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (fromStorage) {
      writeCookie(DEVICE_ID_COOKIE, fromStorage);
      return fromStorage;
    }
  } catch {
    // Ignore storage access failures.
  }

  const fromCookie = readCookie(DEVICE_ID_COOKIE);
  if (fromCookie) {
    try {
      window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, fromCookie);
    } catch {
      // Ignore storage write failures.
    }
    return fromCookie;
  }

  return null;
}

export function ensureDeviceId(): string {
  const existing = getDeviceId();
  if (existing) return existing;

  const created = createDeviceId();
  return persistDeviceId(created);
}

export function clearDeviceFlowState(options?: { preserveDeviceId?: boolean }) {
  const preserveDeviceId = options?.preserveDeviceId !== false;

  DEVICE_FLOW_COOKIES.forEach((cookieName) => clearCookie(cookieName));

  if (!preserveDeviceId) {
    clearCookie(DEVICE_ID_COOKIE);
    try {
      window.localStorage.removeItem(DEVICE_ID_STORAGE_KEY);
    } catch {
      // Ignore storage cleanup failures.
    }
  }
}

export async function hashDeviceTokenClient(deviceToken: string) {
  return hashDeviceTokenWithSaltClient(deviceToken, DEFAULT_DEVICE_TRUST_HASH_SALT);
}

export function getCityFromTimeZone(timeZone: string | null | undefined) {
  const normalizedTimeZone = String(timeZone || '').trim();
  if (!normalizedTimeZone) return null;
  if (/^(etc\/|utc$|gmt)/i.test(normalizedTimeZone)) return null;

  const segments = normalizedTimeZone.split('/').filter(Boolean);
  const citySegment = segments.at(-1);
  if (!citySegment) return null;

  return citySegment
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ');
}

export function buildDeviceDescriptor() {
  if (typeof navigator === 'undefined') {
    return {
      deviceName: 'Dispositivo desconhecido',
      browserName: 'Desconhecido',
      platformName: 'Desconhecido',
      timeZone: 'UTC',
    };
  }

  const ua = navigator.userAgent || '';
  const navigatorWithUaData = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  const platform =
    navigatorWithUaData.userAgentData?.platform || navigator.platform || 'Desconhecido';

  let browserName = 'Navegador';
  if (/edg/i.test(ua)) browserName = 'Edge';
  else if (/chrome|crios/i.test(ua)) browserName = 'Chrome';
  else if (/firefox|fxios/i.test(ua)) browserName = 'Firefox';
  else if (/safari/i.test(ua) && !/chrome|crios|android/i.test(ua)) browserName = 'Safari';

  let platformName = platform;
  if (/iphone|ipad|ios/i.test(ua)) platformName = 'iOS';
  else if (/android/i.test(ua)) platformName = 'Android';
  else if (/win/i.test(platform)) platformName = 'Windows';
  else if (/mac/i.test(platform)) platformName = 'macOS';
  else if (/linux/i.test(platform)) platformName = 'Linux';

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const language = navigator.language || 'unknown';
  const hardwareConcurrency = navigator.hardwareConcurrency || 0;
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const screenWidth = typeof window !== 'undefined' ? window.screen?.width || 0 : 0;
  const screenHeight = typeof window !== 'undefined' ? window.screen?.height || 0 : 0;
  const colorDepth = typeof window !== 'undefined' ? window.screen?.colorDepth || 0 : 0;
  const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const browserFingerprint = [
    browserName,
    platformName,
    timeZone,
    language,
    String(hardwareConcurrency),
    String(maxTouchPoints),
    `${screenWidth}x${screenHeight}`,
    String(colorDepth),
    String(Math.round(devicePixelRatio * 100)),
  ].join('|');

  return {
    deviceName: `${browserName} em ${platformName}`,
    browserName,
    platformName,
    timeZone,
    browserFingerprint,
  };
}